// ─────────────────────────────────────────────────────────────────────────────
// texture_layer.ts — Post-process grain composite layer
//
// Composited OVER all page content in two fast blits:
//   Pass A: soft-light grain at grainAlpha
//   Pass B: combined absorption + vignette + tint at source-over
//
// Exposes the grain canvas for per-stroke texture knockout (brush engine).
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';

// ── Seeded RNG ────────────────────────────────────────────────────────────

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Grain palette ─────────────────────────────────────────────────────────

interface GrainTheme {
  warm: string;
  cool: string;
  grainAlpha: number;
  absorptionAlpha: number;
  fibres: number;
  spots: number;
  tint: string;
  tintAlpha: number;
}

const DEFAULT_THEME: GrainTheme = {
  warm: '#9a9080', cool: '#707878',
  grainAlpha: 0.10, absorptionAlpha: 0.03,
  fibres: 80, spots: 14,
  tint: '#f0d8a0', tintAlpha: 0.06,
};

function themeForColor(color: string | null): GrainTheme {
  if (!color) return DEFAULT_THEME;
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  if (r > 0.55 && g < 0.55 && b < 0.45) {
    return { warm: '#a09070', cool: '#686060', grainAlpha: 0.11, absorptionAlpha: 0.035, fibres: 100, spots: 14, tint: '#e8cfa0', tintAlpha: 0.07 };
  }
  if (g > r && g > b) {
    return { warm: '#788878', cool: '#687070', grainAlpha: 0.09, absorptionAlpha: 0.025, fibres: 50, spots: 8, tint: '#d0e8c8', tintAlpha: 0.05 };
  }
  if (b > r && b > g * 0.9) {
    return { warm: '#787888', cool: '#686878', grainAlpha: 0.08, absorptionAlpha: 0.025, fibres: 45, spots: 7, tint: '#c8d8f0', tintAlpha: 0.05 };
  }
  if (r > 0.45 && b > 0.35 && g < 0.5) {
    return { warm: '#887880', cool: '#706068', grainAlpha: 0.09, absorptionAlpha: 0.025, fibres: 55, spots: 10, tint: '#e0c8e0', tintAlpha: 0.05 };
  }
  return DEFAULT_THEME;
}

// ── Pre-composited overlay builder ────────────────────────────────────────
// Merges grain (soft-light), absorption (source-over), vignette (multiply),
// and warm tint into a single OffscreenCanvas that is blitted once per frame.

function buildOverlay(w: number, h: number, theme: GrainTheme, seed: number): OffscreenCanvas {
  const oc = new OffscreenCanvas(w, h);
  const ctx = oc.getContext('2d')!;
  const rng = mulberry32(seed);

  // Layer 1 — Neutral-grey grain base (for soft-light in the main composite)
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, w, h);

  // Noise blobs
  ctx.globalAlpha = 0.12;
  for (let i = 0; i < 60; i++) {
    const bx = rng() * w, by = rng() * h, br = 10 + rng() * 45;
    const grd = ctx.createRadialGradient(bx, by, 0, bx, by, br);
    grd.addColorStop(0, rng() > 0.5 ? theme.warm : theme.cool);
    grd.addColorStop(1, '#808080');
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(bx, by, br, 0, Math.PI * 2); ctx.fill();
  }
  // Fibrous streaks
  ctx.globalAlpha = 0.08;
  for (let i = 0; i < theme.fibres; i++) {
    const fy = rng() * h, fx0 = rng() * w * 0.2, fx1 = w - rng() * w * 0.2;
    ctx.strokeStyle = rng() > 0.5 ? theme.warm : theme.cool;
    ctx.lineWidth = 0.3 + rng() * 0.6;
    ctx.beginPath(); ctx.moveTo(fx0, fy);
    ctx.bezierCurveTo(
      fx0 + (fx1 - fx0) * 0.3, fy + (rng() - 0.5) * 5,
      fx0 + (fx1 - fx0) * 0.7, fy + (rng() - 0.5) * 5,
      fx1, fy,
    );
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // We now have the soft-light grain texture.  Next layers are composited
  // on a *second* canvas (the overlay) that the main canvas will blit once.
  const overlay = new OffscreenCanvas(w, h);
  const ov = overlay.getContext('2d')!;

  // Base: blit grain with soft-light onto a transparent overlay
  // (soft-light will be applied when we blit the overlay onto the main canvas)
  // We store the grain image separately on the overlay object so the caller
  // can apply the correct composite ops.  For the merged approach we instead:
  //
  // The overlay is blitted in two passes by the caller:
  //   pass A: soft-light at grainAlpha  (the grain canvas)
  //   pass B: source-over at 1          (the combined absorption+vignette+tint)
  //
  // That's still 2 blits (down from 4).  We build "pass B" here:

  ov.clearRect(0, 0, w, h);

  // Absorption speckles
  const rng2 = mulberry32(seed + 777);
  for (let i = 0; i < theme.spots; i++) {
    const sx = rng2() * w, sy = rng2() * h, sr = 1.5 + rng2() * 5;
    ov.globalAlpha = (0.15 + rng2() * 0.15) * theme.absorptionAlpha * 40;
    const grd = ov.createRadialGradient(sx, sy, 0, sx, sy, sr);
    grd.addColorStop(0, '#3a2810'); grd.addColorStop(1, 'transparent');
    ov.fillStyle = grd;
    ov.beginPath(); ov.arc(sx, sy, sr, 0, Math.PI * 2); ov.fill();
  }
  ov.globalAlpha = 0.12 * theme.absorptionAlpha * 40;
  for (let i = 0; i < 120; i++) {
    const sx = rng2() * w, sy = rng2() * h;
    ov.fillStyle = `rgba(40, 20, 5, ${0.5 + rng2() * 0.5})`;
    ov.fillRect(sx, sy, 0.4 + rng2() * 0.8, 0.4 + rng2() * 0.8);
  }
  ov.globalAlpha = 0.06 * theme.absorptionAlpha * 40;
  for (let i = 0; i < 40; i++) {
    const fx = rng2() * w, fy = rng2() * h, len = 3 + rng2() * 12;
    const angle = (rng2() - 0.5) * 0.3;
    ov.strokeStyle = '#2a1808'; ov.lineWidth = 0.2 + rng2() * 0.3;
    ov.beginPath(); ov.moveTo(fx, fy);
    ov.lineTo(fx + len * Math.cos(angle), fy + len * Math.sin(angle)); ov.stroke();
  }

  // Vignette (multiply-like baked in)
  const cx = w / 2, cy = h / 2, outerR = Math.hypot(cx, cy);
  const grd = ov.createRadialGradient(cx, cy, outerR * 0.45, cx, cy, outerR);
  grd.addColorStop(0, 'transparent');
  grd.addColorStop(1, 'rgba(30, 18, 10, 0.18)');
  ov.globalCompositeOperation = 'source-over';
  ov.globalAlpha = 1;
  ov.fillStyle = grd;
  ov.fillRect(0, 0, w, h);

  // Warm tint
  ov.globalAlpha = theme.tintAlpha;
  ov.fillStyle = theme.tint;
  ov.fillRect(0, 0, w, h);

  ov.globalAlpha = 1;

  // Stash the grain canvas on the overlay for the caller
  (overlay as OverlayCanvas)._grain = oc;
  (overlay as OverlayCanvas)._grainAlpha = theme.grainAlpha;
  return overlay;
}

type OverlayCanvas = OffscreenCanvas & {
  _grain: OffscreenCanvas;
  _grainAlpha: number;
};

// ── TextureLayer class ────────────────────────────────────────────────────

export class TextureLayer {
  private _ctx: CanvasRenderingContext2D;

  // Overlay caches (keyed by section color)
  private _overlayCache = new Map<string, OverlayCanvas>();
  private _cachedW = 0;
  private _cachedH = 0;

  constructor(ctx: CanvasRenderingContext2D) {
    this._ctx = ctx;
  }

  // ── Public API ────────────────────────────────────────────────────────

  /**
   * Per-frame draw call.  Blits the pre-composited overlay (2 passes).
   */
  draw(L: PageLayout, sectionColor: string | null, grainIntensity: number = 1): void {
    const w = L.width;
    const h = L.height;

    // Invalidate caches on page size change
    if (w !== this._cachedW || h !== this._cachedH) {
      this._overlayCache.clear();
      this._cachedW = w;
      this._cachedH = h;
    }

    const overlay = this._getOverlay(sectionColor);
    const ctx = this._ctx;

    // Pass A — Soft-light grain
    ctx.save();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = overlay._grainAlpha * grainIntensity;
    ctx.drawImage(overlay._grain, L.x, L.y, w, h);
    ctx.restore();

    // Pass B — Combined absorption + vignette + tint
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.drawImage(overlay, L.x, L.y, w, h);
    ctx.restore();
  }

  /**
   * Returns the grain OffscreenCanvas for the given section color.
   * Used by the brush engine for per-stroke grain knockout.
   */
  grainCanvas(sectionColor: string | null): OffscreenCanvas {
    return this._getOverlay(sectionColor)._grain;
  }

  // ── Internal ──────────────────────────────────────────────────────────

  private _getOverlay(sectionColor: string | null): OverlayCanvas {
    const key = sectionColor ?? '__default__';
    let overlay = this._overlayCache.get(key);
    if (!overlay) {
      const theme = themeForColor(sectionColor);
      let seed = 0x9e3779b9;
      if (sectionColor) {
        for (let i = 0; i < sectionColor.length; i++) {
          seed = ((seed << 5) - seed + sectionColor.charCodeAt(i)) | 0;
        }
      }
      overlay = buildOverlay(this._cachedW || 670, this._cachedH || 840, theme, seed) as OverlayCanvas;
      this._overlayCache.set(key, overlay);
    }
    return overlay;
  }
}

